import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { ShieldAlert, CheckCircle2, AlertCircle, KeyRound } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SecuritySettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [otpDevEcho, setOtpDevEcho] = useState(false);
  const [source, setSource] = useState('env');
  const [envValue, setEnvValue] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const axiosCfg = { withCredentials: true };

  const fetchSecurity = useCallback(async () => {
    try {
      setLoading(true);
      const r = await axios.get(`${BACKEND_URL}/api/admin/settings/security`, axiosCfg);
      setOtpDevEcho(Boolean(r.data.otp_dev_echo));
      setSource(r.data.otp_dev_echo_source || 'env');
      setEnvValue(Boolean(r.data.otp_dev_echo_env_value));
    } catch (e) {
      console.error('Failed to fetch security settings', e);
      setMessage({ type: 'error', text: 'Failed to load security settings.' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSecurity();
  }, [fetchSecurity]);

  const persistToggle = async (next) => {
    setSaving(true);
    setMessage({ type: '', text: '' });
    try {
      const r = await axios.put(
        `${BACKEND_URL}/api/admin/settings/security`,
        { otp_dev_echo: next },
        axiosCfg,
      );
      if (r.data?.success) {
        setOtpDevEcho(Boolean(r.data.otp_dev_echo));
        setSource(r.data.otp_dev_echo_source || 'db');
        setMessage({
          type: 'success',
          text: next
            ? 'OTP dev echo ENABLED — OTPs are now returned in API responses. Disable before production!'
            : 'OTP dev echo DISABLED — OTPs will no longer leak through API responses.',
        });
        setTimeout(() => setMessage({ type: '', text: '' }), 4000);
      }
    } catch (e) {
      console.error('Failed to save security settings', e);
      setMessage({
        type: 'error',
        text: e.response?.data?.detail || 'Failed to save security settings.',
      });
      await fetchSecurity();
    } finally {
      setSaving(false);
      setConfirmOpen(false);
    }
  };

  const handleToggleRequest = () => {
    const next = !otpDevEcho;
    // Turning ON requires explicit confirmation (it's a security-relevant action)
    if (next) {
      setConfirmOpen(true);
      return;
    }
    persistToggle(next);
  };

  if (loading) {
    return (
      <section
        data-testid="security-settings-card"
        className="bg-white rounded-xl border border-gray-200 p-6 mb-6 shadow-sm"
      >
        <div className="animate-pulse h-24 bg-gray-100 rounded-lg" />
      </section>
    );
  }

  return (
    <section
      data-testid="security-settings-card"
      className="bg-white rounded-xl border border-gray-200 p-6 mb-6 shadow-sm"
    >
      <div className="flex items-start gap-3 mb-4">
        <div className="w-10 h-10 rounded-lg bg-rose-100 flex items-center justify-center flex-shrink-0">
          <ShieldAlert className="w-5 h-5 text-rose-600" />
        </div>
        <div className="min-w-0">
          <h2 className="text-xl font-bold text-gray-900">Security Settings</h2>
          <p className="text-sm text-gray-600 mt-1">
            Sensitive runtime toggles. Changes apply within 5 seconds.
          </p>
        </div>
      </div>

      <div className="border border-gray-200 rounded-lg p-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3 flex-1 min-w-0">
            <div className="w-9 h-9 rounded-md bg-amber-100 flex items-center justify-center flex-shrink-0">
              <KeyRound className="w-4 h-4 text-amber-700" />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="text-base font-semibold text-gray-900">
                  OTP Dev Echo
                </h3>
                {otpDevEcho ? (
                  <span
                    data-testid="otp-dev-echo-status-enabled"
                    className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide bg-rose-100 text-rose-800 rounded"
                  >
                    Enabled — INSECURE
                  </span>
                ) : (
                  <span
                    data-testid="otp-dev-echo-status-disabled"
                    className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide bg-emerald-100 text-emerald-800 rounded"
                  >
                    Disabled — Secure
                  </span>
                )}
                <span
                  data-testid="otp-dev-echo-source"
                  className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-medium bg-gray-100 text-gray-700 rounded"
                  title={source === 'db' ? 'Value is set via Admin UI (DB)' : 'Value is sourced from .env'}
                >
                  source: {source}
                </span>
              </div>
              <p className="text-sm text-gray-600 mt-1">
                Returns the generated OTP in <code className="px-1 py-0.5 bg-gray-100 rounded text-xs">POST /api/auth/request-otp</code> responses.
                Useful in dev when WhatsApp/SMS isn’t wired. <strong>Must be OFF in production</strong>
                — leaving it on lets anyone bypass phone verification by reading the response body.
              </p>
              {source === 'env' && (
                <p className="text-xs text-gray-500 mt-1">
                  Current value comes from <code>.env</code> (<code>OTP_DEV_ECHO={String(envValue)}</code>). Use the toggle to override at runtime.
                </p>
              )}
            </div>
          </div>
          <button
            type="button"
            data-testid="otp-dev-echo-toggle"
            onClick={handleToggleRequest}
            disabled={saving}
            className={`relative inline-flex h-7 w-12 flex-shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-rose-500 disabled:opacity-50 ${
              otpDevEcho ? 'bg-rose-500' : 'bg-gray-300'
            }`}
            aria-checked={otpDevEcho}
            role="switch"
            aria-label="Enable OTP dev echo"
          >
            <span
              className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
                otpDevEcho ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>
      </div>

      {message.text && (
        <div
          data-testid="security-settings-message"
          className={`mt-4 p-3 rounded-lg flex items-start gap-2 text-sm ${
            message.type === 'success'
              ? 'bg-green-50 text-green-800 border border-green-200'
              : 'bg-red-50 text-red-800 border border-red-200'
          }`}
        >
          {message.type === 'success' ? (
            <CheckCircle2 className="w-4 h-4 flex-shrink-0 mt-0.5" />
          ) : (
            <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          )}
          <span>{message.text}</span>
        </div>
      )}

      {confirmOpen && (
        <div
          data-testid="otp-dev-echo-confirm-modal"
          className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => !saving && setConfirmOpen(false)}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-rose-100 flex items-center justify-center flex-shrink-0">
                <ShieldAlert className="w-5 h-5 text-rose-600" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900">Enable OTP Dev Echo?</h3>
                <p className="text-sm text-gray-600 mt-1">
                  Anyone who can call <code className="text-xs">/api/auth/request-otp</code> will see the OTP in the response body.
                  This bypasses phone verification entirely. Only enable in non-production environments.
                </p>
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 mt-6">
              <button
                type="button"
                data-testid="otp-dev-echo-confirm-cancel"
                onClick={() => setConfirmOpen(false)}
                disabled={saving}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                data-testid="otp-dev-echo-confirm-enable"
                onClick={() => persistToggle(true)}
                disabled={saving}
                className="px-4 py-2 text-sm font-bold text-white bg-rose-600 hover:bg-rose-700 rounded-lg disabled:opacity-50"
              >
                {saving ? 'Enabling…' : 'Yes, enable'}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default SecuritySettings;
